import type {
  PriorAuthAllowedActionT,
  PriorAuthorizationAnswerRequestT,
  PriorAuthorizationQuestionT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

export type PriorAuthPrimaryAction = 'answer_question' | 'submit' | 'send_prescription';

export function presentPriorAuthActions(allowed: PriorAuthAllowedActionT[], isPrescriber: boolean) {
  const actions = new Set(allowed);
  const primary: PriorAuthPrimaryAction | null = actions.has('ANSWER_QUESTION')
    ? 'answer_question'
    : actions.has('SUBMIT')
      ? 'submit'
      : actions.has('SEND_PRESCRIPTION')
        ? 'send_prescription'
        : null;
  return {
    primary,
    primaryNeedsPrescriber: !isPrescriber && primary === 'send_prescription',
    upload: actions.has('UPLOAD_ATTACHMENT'),
    cancel: actions.has('CANCEL'),
    appeal: actions.has('APPEAL'),
    selfPay: actions.has('SELF_PAY'),

    endPlan: actions.has('END_PLAN'),
  };
}

export interface PriorAuthAnswerInputs {
  selectedChoices: number[];
  freeTextAnswer: string;
  numericAnswer: string;
  dateAnswer: string;
}

export type PriorAuthAdditionalTextRequirement = 'hidden' | 'optional' | 'required';

export function getPriorAuthAdditionalTextRequirement(
  question: PriorAuthorizationQuestionT,
  selectedChoices: readonly number[]
): PriorAuthAdditionalTextRequirement {
  if (question.AnswerType !== 'Select' && question.AnswerType !== 'Multiselect') {
    return 'hidden';
  }

  const selected = (question.AnswerChoices ?? []).filter(
    (choice) =>
      typeof choice.QuestionChoiceId === 'number' &&
      selectedChoices.includes(choice.QuestionChoiceId)
  );
  if (selected.some((choice) => choice.AdditionalFreeTextRequired === true)) {
    return 'required';
  }
  if (selected.some((choice) => choice.AdditionalFreeTextAllowed === true)) {
    return 'optional';
  }
  return 'hidden';
}

export function buildPriorAuthAnswerPayload(
  question: PriorAuthorizationQuestionT,
  inputs: PriorAuthAnswerInputs
): PriorAuthorizationAnswerRequestT | null {
  const { selectedChoices, freeTextAnswer, numericAnswer, dateAnswer } = inputs;
  switch (question.AnswerType) {
    case 'Select':
    case 'Multiselect': {
      const choices = (question.AnswerChoices ?? []).filter(
        (choice): choice is typeof choice & { QuestionChoiceId: number } =>
          typeof choice.QuestionChoiceId === 'number' &&
          selectedChoices.includes(choice.QuestionChoiceId)
      );
      if (choices.length === 0) {
        return null;
      }
      const additionalText = freeTextAnswer.trim();
      if (choices.some((choice) => choice.AdditionalFreeTextRequired) && !additionalText) {
        return null;
      }
      return {
        PriorAuthorizationQuestionChoices: choices.map((choice) => ({
          PriorAuthorizationQuestionChoiceId: choice.QuestionChoiceId,
          PriorAuthorizationQuestionChoiceText:
            additionalText &&
            (choice.AdditionalFreeTextAllowed || choice.AdditionalFreeTextRequired)
              ? additionalText
              : null,
        })),
      };
    }
    case 'FreeText': {
      const answer = freeTextAnswer.trim();
      return answer ? { FreeTextAnswer: answer } : null;
    }
    case 'Numeric': {
      const parsed = Number(numericAnswer);
      return numericAnswer.trim() && Number.isFinite(parsed) ? { NumericAnswer: parsed } : null;
    }
    case 'Date':
    case 'DateTime':
      return dateAnswer ? { DateTimeAnswer: dateAnswer } : null;
    default:
      return null;
  }
}
